<template>
    <page-container v-if="!loading">
        <step-form-heading v-if="!props.apiDestinationId" :icon="faBookAtlas">
            <div>
                New API Destination
            </div>

            <template v-slot:description>
                Connect Voxie to another service for tighter integrations to your other services.
            </template>
        </step-form-heading>
        <page-header v-if="props.apiDestinationId" class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <a href="/settings/integrations#/api-destinations"
                    class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All API Destinations
                </a>
            </template>

            {{ data.friendly_name }}
        </page-header>
        <div :class="{
            'grid grid-cols-12': props.apiDestinationId,
        }">
            <api-destination-sidebar v-if="props.apiDestinationId" :apiDestinationId="props.apiDestinationId"></api-destination-sidebar>
            <vx-sticky-nav
                :sections="[
                    { id: 'name', label: 'Name' },
                    { id: 'endpoint', label: 'Endpoint' },
                    { id: 'method', label: 'Method' },
                    { id: 'auth', label: 'API Auth' },
                ]"
                class="col-span-12 lg:col-span-8 lg:col-start-3"
                :navClasses="`${props.apiDestinationId ? 'hidden' : ''}`"
            >

                <form @submit.prevent="submit()" id="api-destination-form"
                    class="flex flex-col mx-auto items-center gap-2 md:gap-8 w-full max-w-(--breakpoint-sm)">

                    <vx-expandable-card id="name" >
                        <template v-slot:content>
                            <api-destination-form-name v-model="data.friendly_name"
                                :error="errors?.friendly_name"></api-destination-form-name>
                        </template>
                    </vx-expandable-card>
                    <vx-expandable-card id="endpoint" >
                        <template v-slot:content>
                            <api-destination-form-endpoint v-model="data.uri_template" :disabled="!!data.id"
                                :error="errors?.uri_template"></api-destination-form-endpoint>
                                <vx-button v-if="props.apiDestinationId"
                                    :href="`/settings/integrations/api-destinations/new?duplicateFromId=${props.apiDestinationId}`"
                                    color="muted"
                                    size="sm"
                                    class="mt-4"
                                >
                                    Make a copy <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                                </vx-button>
                        </template>
                    </vx-expandable-card>
                    <vx-expandable-card id="method" >
                        <template v-slot:content>
                            <api-destination-form-http-method v-model="data.http_method"
                                :error="errors?.http_method"></api-destination-form-http-method>
                        </template>
                    </vx-expandable-card>
                    <vx-expandable-card id="auth" >
                        <template v-slot:content>
                            <api-destination-form-auth v-model="data.auth_config_id"
                                :error="errors?.auth_config_id"></api-destination-form-auth>
                        </template>
                    </vx-expandable-card>

                    <vx-sticky-footer >
                        <vx-button href="/settings/integrations#/api-destinations" class="w-20" size="lg"
                            color="muted">
                            Cancel
                        </vx-button>
                        <vx-button form="api-destination-form" type="submit" class="grow" size="lg" color="primary"
                            :loading="processing">
                            {{ props.apiDestinationId ? 'Update' : 'Create' }} API Destination
                        </vx-button>
                    </vx-sticky-footer>
                </form>
            </vx-sticky-nav>
        </div>
    </page-container>
</template>

<script setup>
import {
    VxStickyNav,
    VxStickyFooter,
    VxButton,
    VxExpandableCard,
} from '@voxie/frontend-components';
import StepFormHeading from '../../../../general/StepFormHeading.vue';
import { faBookAtlas, faArrowLeft, faCirclePlus, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ApiDestinationFormName from './ApiDestinationFormName.vue';
import ApiDestinationFormEndpoint from './ApiDestinationFormEndpoint.vue';
import ApiDestinationFormHttpMethod from './ApiDestinationFormHttpMethod.vue';
import ApiDestinationFormAuth from './ApiDestinationFormAuth.vue';
import ApiDestinationSidebar from '../ApiDestinationSidebar.vue';
import PageHeader from '../../../../general/PageHeader.vue';
import PageContainer from '../../../../general/PageContainer.vue';
import { useApiDestinationForm } from "./useApiDestinationForm";
import { useFormSubmission } from '~/composables/useFormSubmission';
import { onMounted } from "vue";
import { useUrlSearchParams } from '@vueuse/core';

const props = defineProps({
    apiDestinationId: {
        type: String,
        required: false,
        default: undefined,
    }
});

const { data, errors, save, loading, duplicate } = useApiDestinationForm();

const resetForm = () => {
    data.value = {
        friendly_name: '',
        http_method: 'POST',
        uri_template: '',
        auth_config_id: ''
    };
};

const { submit, processing } = useFormSubmission({
    data,
    submit: save,
    preserveScroll: () => Boolean(props.apiDestinationId),
    reset: () => {
        if (!props.apiDestinationId) {
            resetForm();
        }
    },
    notification: () => ({
        message: `API Destination ${props.apiDestinationId ? 'updated' : 'created'}`,
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to API Destinations',
            href: '/settings/integrations#/api-destinations'
        }
    })
});

const queryParams = useUrlSearchParams();

onMounted(async () => {
    if (props.apiDestinationId) {
       data.value.id = props.apiDestinationId;
    }

    if (queryParams.duplicateFromId) {
        duplicate(queryParams.duplicateFromId);
    }
});
</script>
